import { useTheme } from '@shopify/restyle';
import { IconCalendar, IconList } from '@tabler/icons-react-native';
import { EmptyGraphic } from 'app/components/empty-graphic';
import { LoanCard } from 'app/components/loan-card';
import { Text } from 'app/design/primitives/text';
import { Column, Row } from 'app/design/primitives/box';
import { ListPage } from 'app/design/primitives/page/list';
import type { Theme } from 'app/shopify';
import type { Loan, LoanType } from 'app/types/loan';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, Pressable, View } from 'react-native';
import Animated, { FadeIn, FadeOut, LinearTransition } from 'react-native-reanimated';
import { LoanItemSeparator } from './loan-item-separator';
import { ResponsiveLoanPlanner } from './responsive-loan-planner';

type MobileTab = 'loans' | 'calendar';

interface ScreenContentProps {
  isLoading: boolean;
  isRefetching: boolean;
  loanType: LoanType;
  loans: Loan[];
  onRefresh: () => void;
  tPrefix: string;
}

export function MobileLoans({ isLoading, isRefetching, loanType, loans, onRefresh, tPrefix }: ScreenContentProps) {
  const theme = useTheme<Theme>();
  const { t } = useTranslation('loans');
  const [activeTab, setActiveTab] = useState<MobileTab>('calendar');

  return (
    <Column flex={1} minHeight={0}>
      <Row
        alignItems="center"
        gap="spacing.general.x3"
        paddingHorizontal="spacing.general.x4"
        paddingVertical="spacing.general.x3"
      >
        <Text flex={1} accessibilityRole="header" variant="bodyLargeMedium">
          {t(`${tPrefix}${loanType}.subtitle`)}
        </Text>
        <View
          style={{
            flexDirection: 'row',
            padding: 2,
            borderRadius: theme.borderRadii['radius.full'],
            backgroundColor: theme.colors['surface.secondary-neutral'],
          }}
        >
          {(['calendar', 'loans'] as const).map((view) => {
            const selected = activeTab === view;
            const Icon = view === 'calendar' ? IconCalendar : IconList;
            return (
              <Pressable
                key={view}
                accessibilityRole="button"
                accessibilityState={{ selected }}
                accessibilityLabel={t(view === 'calendar' ? 'calendar.tabs.calendar' : `calendar.tabs.${loanType}`)}
                onPress={() => setActiveTab(view)}
                style={({ pressed }) => ({
                  width: 44,
                  height: 44,
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: theme.borderRadii['radius.full'],
                  backgroundColor: selected ? theme.colors['text.primary'] : 'transparent',
                  opacity: pressed ? 0.65 : 1,
                })}
              >
                <Icon
                  size={20}
                  strokeWidth={ICON_STROKE_WIDTH}
                  color={theme.colors[selected ? 'text.inverse.primary' : 'text.secondary']}
                />
              </Pressable>
            );
          })}
        </View>
      </Row>

      {activeTab === 'loans' ? (
        <Animated.View
          entering={FadeIn.duration(300)}
          exiting={FadeOut.duration(300)}
          key="loans"
          layout={LinearTransition.duration(300)}
          style={{ flex: 1, minHeight: 0 }}
        >
          <ListPage
            ItemSeparatorComponent={LoanItemSeparator}
            ListEmptyComponent={
              isLoading ? (
                <ActivityIndicator accessibilityLabel={t('calendar.loading')} />
              ) : (
                <EmptyGraphic
                  invertTexts
                  primary={t(`${tPrefix}${loanType}.empty.secondLine`)}
                  secondary={t(`${tPrefix}${loanType}.empty.firstLine`)}
                />
              )
            }
            contentContainerStyle={{ paddingTop: 16 }}
            data={loans}
            keyExtractor={(item) => item.id}
            onRefresh={onRefresh}
            refreshing={isRefetching}
            renderItem={({ item }) => <LoanCard {...item} />}
          />
        </Animated.View>
      ) : (
        <Animated.View
          entering={FadeIn.duration(300)}
          exiting={FadeOut.duration(300)}
          key="calendar"
          layout={LinearTransition.duration(300)}
          style={{ flex: 1, minHeight: 0 }}
        >
          {isLoading && loans.length === 0 ? (
            <ActivityIndicator accessibilityLabel={t('calendar.loading')} />
          ) : (
            <ResponsiveLoanPlanner loans={loans} loanType={loanType} />
          )}
        </Animated.View>
      )}
    </Column>
  );
}
